import { css } from '@emotion/react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { configSectionCss } from '../components/configSectionCss';
import { ModeCopy } from './roundCreationCopy';

/**
 * The shape every setting on this page takes, following the prototype: a card per section under a
 * small uppercase eyebrow, and inside it a title with an explanation in words a client understands,
 * the control either on the right (a switch) or stacked underneath (a choice).
 *
 * The prototype's type scale is denser than the MUI one (10.5/11.5/13px), so the sizes below are
 * literal rather than variants; every colour maps to a theme token.
 */

/** The uppercase eyebrow a card is titled with, and the same label reused above a radio group. */
export const eyebrowCss = css`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--mui-colors-neutral-50);
`;

const sectionHeadingCss = css`
  ${eyebrowCss};
  margin: 0 0 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--mui-colors-border-card);
`;

// Dashed, so a setting reads as a subdivision of the card rather than another card.
const settingCss = css`
  padding: 12px 0;
  border-bottom: 1px dashed var(--mui-colors-border-card);
`;

const sectionCss = css`
  ${configSectionCss};
  /* The card's own edge ends the last row. Position alone cannot say which row that is: a Collapse
     can hold it, and a collapsed one must not leave a rule dangling above nothing. */
  & > [data-setting]:last-child,
  & > [data-setting]:has(+ .MuiCollapse-hidden:last-child),
  & > :last-child [data-setting]:last-child {
    border-bottom: none;
  }
`;

const settingTitleCss = css`
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
`;

export const helpCss = css`
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--mui-colors-text-secondary);
  margin-top: 4px;
  max-width: 600px;
`;

const subLabelRowCss = css`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const keyChipCss = css`
  font-family: monospace;
  font-size: 9px;
  line-height: 1.4;
  color: var(--mui-colors-neutral-40);
  background: var(--mui-colors-neutral-5);
  border: 1px solid var(--mui-colors-neutral-10);
  border-radius: 4px;
  padding: 1px 5px;
  text-transform: none;
  letter-spacing: 0;
`;

const infoBoxCss = css`
  font-size: 11px;
  line-height: 1.6;
  color: var(--mui-colors-text-secondary);
  background: var(--mui-colors-neutral-5);
  border: 1px dashed var(--mui-colors-neutral-20);
  border-radius: 8px;
  padding: 8px 10px;
  margin-top: 12px;
`;

const condNoteCss = css`
  font-size: 10.5px;
  line-height: 1.5;
  color: var(--mui-colors-neutral-40);
  font-style: italic;
  margin-top: 6px;
`;

const inheritedNoteCss = css`
  display: flex;
  gap: 6px;
  align-items: baseline;
  font-size: 11px;
  line-height: 1.55;
  color: var(--mui-colors-text-secondary);
  margin: 10px 0 0;
  padding-left: 9px;
  border-left: 2px solid var(--mui-colors-neutral-20);

  & > span:first-of-type {
    flex: none;
    font-weight: 600;
    color: var(--mui-colors-neutral-50);
  }
`;

const radiosCss = css`
  gap: 9px;
  margin-top: 8px;
`;

const chipRowCss = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const chipCss = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--mui-colors-neutral-20);
  border-radius: 20px;
  background: var(--mui-colors-primary-0);
  color: var(--mui-colors-text-secondary);
  padding: 4.5px 12px 4.5px 9px;
  font-size: 11.5px;
  cursor: pointer;
`;

const chipSelectedCss = css`
  background: var(--mui-colors-secondary-50);
  border-color: var(--mui-colors-secondary-50);
  color: var(--mui-colors-primary-0);
`;

const chipBoxCss = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 11px;
  height: 11px;
  border: 1px solid currentColor;
  border-radius: 3px;
`;

const linkButtonCss = css`
  border: none;
  background: none;
  padding: 0;
  font-size: 11px;
  color: var(--mui-colors-secondary-50);
  cursor: pointer;
  text-decoration: underline;

  &:disabled {
    color: var(--mui-colors-neutral-40);
    cursor: default;
    text-decoration: none;
  }
`;

export function SettingsSection({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: string;
  children: ReactNode;
}) {
  return (
    <section css={sectionCss}>
      <h2 css={sectionHeadingCss}>{title}</h2>
      {intro && <p css={helpCss}>{intro}</p>}
      {children}
    </section>
  );
}

/** The config key a setting maps to, so CS can name it on a call without opening the API. */
export function KeyChip({ name }: { name: string }) {
  return <code css={keyChipCss}>{name}</code>;
}

/** A small uppercase heading above a group of controls, optionally naming its config key. */
export function SubLabel({
  children,
  keyChip,
  tip,
}: {
  children: ReactNode;
  keyChip?: string;
  tip?: string;
}) {
  return (
    <div css={[eyebrowCss, subLabelRowCss]}>
      <span>{children}</span>
      {keyChip && <KeyChip name={keyChip} />}
      {tip && <InfoTip text={tip} />}
    </div>
  );
}

/**
 * The engine detail behind a setting, one hover away. It belongs on the page rather than in a
 * runbook: the person changing a client's grouping rule is the person who needs to know that
 * stage comes from the position name.
 */
export function InfoTip({ text }: { text: string }) {
  return (
    <Tooltip title={text} arrow>
      {/* titleAccess, not aria-label: MUI renders a bare icon aria-hidden, which drops the
          explanation from the accessibility tree. tabIndex makes the tooltip reachable by key. */}
      <InfoOutlinedIcon
        titleAccess={text}
        tabIndex={0}
        sx={{ fontSize: '12px', color: 'var(--mui-colors-neutral-40)', cursor: 'help' }}
      />
    </Tooltip>
  );
}

/** Why a control is currently inert, said in place rather than by hiding it without explanation. */
export function CondNote({ children }: { children: ReactNode }) {
  return <p css={condNoteCss}>{children}</p>;
}

/**
 * What governs a dial nobody has chosen yet. One quiet line under the options: a pill and a
 * sentence side by side competed with the choices above them for the same attention, and the
 * sentence wrapped around the pill at every width.
 */
export function InheritedNote({ label, children }: { label: string; children: ReactNode }) {
  return (
    <p css={inheritedNoteCss}>
      <span>{label}</span>
      {children}
    </p>
  );
}

/** A dashed note at the foot of a card: context, not a setting. */
export function InfoBox({ children }: { children: ReactNode }) {
  return <div css={infoBoxCss}>{children}</div>;
}

/** Title and explanation on the left, the switch on the right. */
function SwitchRow({
  title,
  help,
  checked,
  onChange,
  disabled,
  keyChip,
  tip,
}: {
  title: string;
  help: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  keyChip?: string;
  tip?: string;
}) {
  return (
    <Stack direction='row' gap='1.5rem' alignItems='flex-start' justifyContent='space-between'>
      <div>
        <div css={[settingTitleCss, subLabelRowCss]}>
          <span>{title}</span>
          {keyChip && <KeyChip name={keyChip} />}
          {tip && <InfoTip text={tip} />}
        </div>
        <p css={helpCss}>{help}</p>
      </div>
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        inputProps={{ 'aria-label': title }}
      />
    </Stack>
  );
}

export function SwitchSetting(props: {
  title: string;
  help: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  keyChip?: string;
  tip?: string;
}) {
  return (
    <div css={settingCss} data-setting>
      <SwitchRow {...props} />
    </div>
  );
}

/** A choice between named options, each carrying the sentence that explains it. */
export function ChoiceSetting<T extends string>({
  title,
  help,
  options,
  copy,
  value,
  onChange,
  footnote,
  keyChip,
  tip,
  condNote,
}: {
  title: string;
  help?: string;
  options: T[];
  copy: Record<T, ModeCopy>;
  /** Null selects nothing: an unchosen dial with no resolvable fallback has no value to show. */
  value: T | null;
  onChange: (next: T) => void;
  footnote?: ReactNode;
  keyChip?: string;
  tip?: string;
  condNote?: string;
}) {
  return (
    <div css={settingCss} data-setting>
      <SubLabel keyChip={keyChip} tip={tip}>
        {title}
      </SubLabel>
      {help && <p css={helpCss}>{help}</p>}
      <RadioGroup
        aria-label={title}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value as T)}
        css={radiosCss}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option}
            value={option}
            // A dial showing an inherited value is on that value without having chosen it, so
            // clicking it fires no change. The click is the choice; setting the same value twice
            // on a real change is a no-op.
            control={<Radio size='small' onClick={() => onChange(option)} />}
            sx={{ alignItems: 'flex-start', mr: 0 }}
            label={
              <div>
                <div css={settingTitleCss}>{copy[option].label}</div>
                <p css={helpCss}>{copy[option].description}</p>
              </div>
            }
          />
        ))}
      </RadioGroup>
      {condNote && <CondNote>{condNote}</CondNote>}
      {footnote}
    </div>
  );
}

/** A number with its unit, and the sentence that says what the number means. */
export function NumberSetting({
  title,
  help,
  unit,
  value,
  onChange,
  error,
  min,
  max,
  keyChip,
  tip,
}: {
  title: string;
  help: string;
  unit: string;
  value: number | null;
  onChange: (next: number | null) => void;
  error?: string;
  min: number;
  max: number;
  keyChip?: string;
  tip?: string;
}) {
  return (
    <div css={settingCss} data-setting>
      <SubLabel keyChip={keyChip} tip={tip}>
        {title}
      </SubLabel>
      <Stack direction='row' gap='0.75rem' alignItems='center' mt='0.5rem'>
        <TextField
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          type='number'
          size='small'
          inputProps={{ min, max, step: 1, 'aria-label': title }}
          error={!!error}
          helperText={error}
          sx={{ width: '9rem' }}
        />
        <Typography variant='body2' color='text.secondary'>
          {unit}
        </Typography>
      </Stack>
      <p css={helpCss}>{help}</p>
    </div>
  );
}

/** One security, as a pill that carries its own selected state. */
function SecurityChip({
  label,
  selected,
  onToggle,
}: {
  label: string;
  selected: boolean;
  onToggle: () => void;
}) {
  // An MUI Chip rather than a bare button, so the app's Chip theme and its keyboard handling reach
  // this control too. `UnstyledChipSelector` is not the reuse here: its contract is single-select
  // (`value: T | null`), and each security is independently on or off, which is a checkbox.
  return (
    <Chip
      role='checkbox'
      aria-checked={selected}
      clickable
      onClick={onToggle}
      label={label}
      icon={
        <span css={chipBoxCss} aria-hidden>
          {selected && <CheckRoundedIcon sx={{ fontSize: '9px' }} />}
        </span>
      }
      css={[chipCss, selected && chipSelectedCss]}
    />
  );
}

/**
 * The securities list as pills. Null means every kind may create and shows as all selected; the
 * first click on that state turns it into an explicit list minus the one clicked, so nothing
 * silently stops creating rounds the moment the client touches the control.
 */
export function SecurityTypesSetting({
  title,
  help,
  selectAllLabel,
  options,
  labelOf,
  value,
  onChange,
  error,
  keyChip,
  tip,
}: {
  title: string;
  help: string;
  selectAllLabel: string;
  options: string[];
  labelOf: (type: string) => string;
  value: string[] | null;
  onChange: (next: string[] | null) => void;
  error?: string;
  keyChip?: string;
  tip?: string;
}) {
  const allowAll = value === null;
  const selected = value ?? options;
  const toggle = (type: string) => {
    onChange(selected.includes(type) ? selected.filter((t) => t !== type) : [...selected, type]);
  };
  return (
    <div css={settingCss} data-setting>
      <Stack direction='row' alignItems='center' justifyContent='space-between' gap='1rem'>
        <SubLabel keyChip={keyChip} tip={tip}>
          {title}
        </SubLabel>
        <button type='button' css={linkButtonCss} onClick={() => onChange(null)} disabled={allowAll}>
          {selectAllLabel}
        </button>
      </Stack>
      <p css={helpCss}>{help}</p>
      <div css={chipRowCss}>
        {options.map((type) => (
          <SecurityChip
            key={type}
            label={labelOf(type)}
            selected={selected.includes(type)}
            onToggle={() => toggle(type)}
          />
        ))}
      </div>
      {error && (
        <Typography variant='caption' color='error'>
          {error}
        </Typography>
      )}
    </div>
  );
}
